import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { 
  BookOpen, Heart, Calendar, User, Image, Send, Plus, X, 
  Clock, CheckCircle, ChevronRight, Star, MapPin, Trophy
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import ImageUpload from "../components/ImageUpload";

const categoryInfo = {
  upton_park: { label: "Upton Park/Boleyn Ground", icon: "🏟️", color: "bg-amber-500" },
  european: { label: "European Nights", icon: "🌟", color: "bg-blue-500" },
  world_cup: { label: "1966 World Cup", icon: "🏆", color: "bg-yellow-500" },
  away_days: { label: "Away Days", icon: "🚌", color: "bg-green-500" },
  derby: { label: "Derby Days", icon: "⚔️", color: "bg-red-500" },
  legends: { label: "Club Legends", icon: "👑", color: "bg-purple-500" },
  general: { label: "General Memories", icon: "💭", color: "bg-slate-500" }
};

// Memory Card Component
const MemoryCard = ({ memory, onClick }) => {
  const cat = categoryInfo[memory.category] || categoryInfo.general;
  const inner = (
    <>
      {/* Image or Placeholder */}
      <div className="relative h-48 bg-gradient-to-br from-[#7C2C3B] to-[#5a1f2b] flex items-center justify-center">
        {memory.image_url ? (
          <img 
            src={memory.image_url} 
            alt={memory.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-6xl opacity-50">{cat.icon}</div>
        )}
        {memory.is_featured && (
          <div className="absolute top-3 right-3 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
            <Star className="w-3 h-3" /> Featured
          </div>
        )}
        <div className={`absolute bottom-3 left-3 ${cat.color} text-white px-3 py-1 rounded-full text-xs font-medium`}>
          {cat.icon} {cat.label}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-5">
        <h3 className="text-lg font-bold text-[#0F172A] mb-2 line-clamp-2 group-hover:text-[#7C2C3B] transition-colors" style={{ fontFamily: 'Barlow Condensed' }}>
          {memory.title}
        </h3>
        <p className="text-slate-600 text-sm line-clamp-3 mb-4">
          {memory.content.substring(0, 150)}...
        </p>
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <User className="w-3 h-3" />
            <span>{memory.author_name}</span>
          </div>
          <div className="flex items-center gap-1 text-[#7C2C3B] font-medium">
            Read Story <ChevronRight className="w-3 h-3" />
          </div>
        </div>
      </div>
    </>
  );

  // Prefer a real anchor-based link for SEO/crawlers when we have a slug
  if (memory.slug) {
    return (
      <Link
        to={`/fan-memories/${memory.slug}`}
        className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer group block"
        data-testid={`memory-card-${memory.id}`}
      >
        {inner}
      </Link>
    );
  }

  // Fallback: legacy modal open if no slug yet
  return (
    <div 
      onClick={onClick}
      className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
      data-testid={`memory-card-${memory.id}`}
    >
      {inner}
    </div>
  );
};

// Memory Detail Modal
const MemoryModal = ({ memory, onClose }) => {
  if (!memory) return null;
  const cat = categoryInfo[memory.category] || categoryInfo.general;
  
  return (
    <Dialog open={!!memory} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        {memory.image_url && (
          <div className="relative h-64 -mx-6 -mt-6 mb-6">
            <img 
              src={memory.image_url} 
              alt={memory.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className={`absolute bottom-4 left-4 ${cat.color} text-white px-3 py-1 rounded-full text-sm font-medium`}>
              {cat.icon} {cat.label}
            </div>
          </div>
        )}
        
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0F172A] leading-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            {memory.title}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" /> {memory.author_name}
            </span>
            {!memory.image_url && (
              <span className={`${cat.color} text-white px-2 py-0.5 rounded text-xs`}>
                {cat.icon} {cat.label}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4 prose prose-slate max-w-none">
          {memory.content.split('\n\n').map((paragraph, idx) => (
            <p key={idx} className="text-slate-700 leading-relaxed mb-4">
              {paragraph}
            </p>
          ))}
        </div>
        
        <div className="mt-6 pt-4 border-t border-slate-200 flex justify-end">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Submit Memory Dialog
const SubmitMemoryDialog = ({ open, onOpenChange, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "",
    image_url: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.category || !formData.content) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.content.length < 50) {
      toast.error("Your story must be at least 50 characters");
      return;
    }

    if (formData.content.length > 4000) {
      toast.error("Your story exceeds the maximum length (approximately 500 words)");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/fan-memories/submit`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Your memory has been submitted for review!");
      setFormData({ title: "", content: "", category: "", image_url: "" });
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit memory");
    } finally {
      setLoading(false);
    }
  };

  const wordCount = formData.content.split(/\s+/).filter(w => w.length > 0).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
            SHARE YOUR MEMORY
          </DialogTitle>
          <DialogDescription>
            Share your West Ham story with fellow Hammers fans. Tell us about your favourite moments, matchday memories, or what the club means to you.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              placeholder="e.g., My First Game at Upton Park"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              data-testid="submit-memory-title"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="category">Category *</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger data-testid="submit-memory-category">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(categoryInfo).map(([key, info]) => (
                  <SelectItem key={key} value={key}>
                    {info.icon} {info.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label htmlFor="content">Your Story *</Label>
              <span className={`text-xs ${wordCount > 500 ? 'text-red-500' : 'text-slate-500'}`}>
                {wordCount}/500 words
              </span>
            </div>
            <Textarea
              id="content"
              placeholder="Share your memory... What happened? How did it make you feel? Why does it matter to you?"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={10}
              required
              data-testid="submit-memory-content"
            />
            <p className="text-xs text-slate-500">
              Write up to 500 words about your West Ham memory. Be descriptive - help other fans feel like they were there!
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="image" className="flex items-center gap-2">
              <Image className="w-4 h-4" /> Photo (optional)
            </Label>
            <ImageUpload
              value={formData.image_url}
              onChange={(url) => setFormData({ ...formData, image_url: url })}
              label="Upload a photo for your memory"
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#7C2C3B] hover:bg-[#63232F]"
              disabled={loading}
              data-testid="submit-memory-button"
            >
              {loading ? "Submitting..." : "Submit for Review"}
              <Send className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Submit Memory Card
const SubmitMemoryCard = ({ onClick }) => (
  <div 
    onClick={onClick}
    className="bg-gradient-to-br from-[#2BAEE5] to-[#1a8fc4] rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition-all group"
    data-testid="submit-memory-card"
  >
    <div className="flex items-start gap-4">
      <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
        <Plus className="w-7 h-7" />
      </div>
      <div className="flex-1">
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
          SHARE YOUR MEMORY
        </h3>
        <p className="text-white/80 text-sm mb-3">
          Got a story to tell? Share your favourite West Ham memory with fans around the world.
        </p>
        <div className="flex items-center gap-2 text-sm font-medium">
          <span>Submit Your Story</span>
          <Plus className="w-4 h-4" />
        </div>
      </div>
    </div>
  </div>
);

export default function FanMemoriesPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ memories: [], categories: {}, category_counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [mySubmissions, setMySubmissions] = useState([]);

  useEffect(() => {
    fetchMemories();
    if (user) {
      fetchMySubmissions();
    }
  }, [user, selectedCategory]);

  const fetchMemories = async () => {
    try {
      const params = selectedCategory !== "all" ? `?category=${selectedCategory}` : "";
      const response = await axios.get(`${API}/fan-memories${params}`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch memories:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMySubmissions = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const response = await axios.get(`${API}/fan-memories/submissions/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMySubmissions(response.data.submissions || []);
    } catch (error) {
      console.error("Failed to fetch submissions:", error);
    }
  };

  const featuredMemories = data.memories.filter(m => m.is_featured);
  const regularMemories = data.memories.filter(m => !m.is_featured);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fan-memories-page">
      <SEO title="Fan Memories" path="/fan-memories" description="Share and relive your favourite West Ham United memories. Stories from match days, Upton Park, the Boleyn Ground and beyond from fellow Hammers supporters." />
      <Navbar />
      
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-[#7C2C3B] via-[#8B3A4A] to-[#5a1f2b] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
            <BookOpen className="w-5 h-5 text-[#F5D76E]" />
            <span className="text-white/90 text-sm font-medium">Stories from the Hammers Family</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            FAN MEMORIES
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Relive the magic through the eyes of fellow supporters. From Upton Park to Wembley, 
            from 1966 to today - these are your stories.
          </p>
          
          {/* Stats */}
          <div className="flex justify-center gap-8 mt-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#F5D76E]" style={{ fontFamily: 'Barlow Condensed' }}>
                {data.total}
              </div>
              <div className="text-white/60 text-sm">Stories</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#F5D76E]" style={{ fontFamily: 'Barlow Condensed' }}>
                {Object.keys(data.categories).length}
              </div>
              <div className="text-white/60 text-sm">Categories</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#F5D76E]" style={{ fontFamily: 'Barlow Condensed' }}>
                130+
              </div>
              <div className="text-white/60 text-sm">Years of Memories</div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto scrollbar-hide py-4 gap-2" data-testid="category-filters">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedCategory === "all"
                  ? "bg-[#7C2C3B] text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="category-filter-all"
            >
              All Stories
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {data.total}
              </span>
            </button>
            {Object.entries(categoryInfo).map(([key, info]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  selectedCategory === key
                    ? `${info.color} text-white`
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`category-filter-${key}`}
              >
                {info.icon} {info.label}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  selectedCategory === key ? "bg-white/20" : "bg-slate-200"
                }`}>
                  {data.category_counts[key] || 0}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Share Your Memory - Top of page */}
        {!loading && (
          <div className="mb-8">
            <SubmitMemoryCard 
              onClick={() => {
                if (!user) {
                  toast.error("Please log in to submit a memory");
                  return;
                }
                setShowSubmitDialog(true);
              }} 
            />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7C2C3B]"></div>
          </div>
        ) : data.memories.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              NO MEMORIES YET
            </h3>
            <p className="text-slate-500 mb-6">
              Be the first to share your West Ham story!
            </p>
            <Button 
              onClick={() => user ? setShowSubmitDialog(true) : toast.error("Please log in to submit a memory")}
              className="bg-[#7C2C3B] hover:bg-[#63232F]"
            >
              <Plus className="w-4 h-4 mr-2" /> Share Your Memory
            </Button>
          </div>
        ) : (
          <>
            {/* Featured Stories */}
            {selectedCategory === "all" && featuredMemories.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  <Star className="w-6 h-6 text-yellow-500" /> FEATURED STORIES
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {featuredMemories.map(memory => (
                    <MemoryCard 
                      key={memory.id} 
                      memory={memory} 
                      onClick={() => setSelectedMemory(memory)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All Stories */}
            <div>
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
                {selectedCategory === "all" ? "ALL STORIES" : categoryInfo[selectedCategory]?.label.toUpperCase()}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(selectedCategory === "all" ? regularMemories : data.memories).map(memory => (
                  <MemoryCard 
                    key={memory.id} 
                    memory={memory} 
                    onClick={() => setSelectedMemory(memory)}
                  />
                ))}
              </div>
            </div>

            {/* Submit Section */}
            <div className="mt-16">
              <div className="grid md:grid-cols-1 gap-6">
                {/* My Submissions */}
                {user && mySubmissions.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                      YOUR SUBMISSIONS
                    </h3>
                    <div className="space-y-3">
                      {mySubmissions.slice(0, 3).map(sub => (
                        <div key={sub.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-[#0F172A] truncate">{sub.title}</div>
                            <div className="text-xs text-slate-500">
                              {categoryInfo[sub.category]?.icon} {categoryInfo[sub.category]?.label}
                            </div>
                          </div>
                          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ml-3 ${
                            sub.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {sub.status === 'pending' && <Clock className="w-3 h-3" />}
                            {sub.status === 'approved' && <CheckCircle className="w-3 h-3" />}
                            {sub.status === 'rejected' && <X className="w-3 h-3" />}
                            {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                          </div>
                        </div>
                      ))}
                      {mySubmissions.length > 3 && (
                        <p className="text-xs text-slate-500 text-center">
                          + {mySubmissions.length - 3} more submissions
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Memory Detail Modal */}
      <MemoryModal memory={selectedMemory} onClose={() => setSelectedMemory(null)} />

      {/* Submit Memory Dialog */}
      <SubmitMemoryDialog 
        open={showSubmitDialog} 
        onOpenChange={setShowSubmitDialog}
        onSuccess={() => {
          fetchMySubmissions();
          fetchMemories();
        }}
      />

      <Footer />
    </div>
  );
}
